/**
 * Main App Component
 *
 * Sets up routing and authentication flow.
 * Uses React Router for client-side navigation.
 */

import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useAuth } from './hooks';
import { LoginPage, ServicesPage, ProfilePage, AdminUsersPage, AuthCallbackPage, LogoutRedirectPage } from './pages';

/**
 * Protected Route Component
 *
 * Wrapper that checks authentication before rendering route.
 * If not authenticated, redirects to login page.
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <div style={{ fontSize: '16px', color: '#5f6368' }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/**
 * Admin Route Component
 *
 * Protected route that also checks for admin role.
 */
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { canAccessAdmin } = useAuth();

  if (!canAccessAdmin) {
    return <Navigate to="/services" replace />;
  }

  return <>{children}</>;
};

/**
 * Main App Component
 */
export const App: React.FC = () => {
  const { loadUserFromStorage } = useAuthStore();

  // Initialize SSE connection for real-time auth sync
  useAuth();

  // Load user from localStorage on app startup
  useEffect(() => {
    loadUserFromStorage();
  }, [loadUserFromStorage]);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/logout-redirect" element={<LogoutRedirectPage />} />

        {/* Protected Routes */}
        <Route
          path="/services"
          element={
            <ProtectedRoute>
              <ServicesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminUsersPage />
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        {/* Redirect root to services */}
        <Route path="/" element={<Navigate to="/services" replace />} />

        {/* 404 Not Found */}
        <Route
          path="*"
          element={
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              backgroundColor: '#f8f9fa',
            }}>
              <div style={{ fontSize: '72px', marginBottom: '16px' }}>404</div>
              <div style={{ fontSize: '24px', color: '#5f6368', marginBottom: '32px' }}>
                Page not found
              </div>
              <a href="/services" style={{ color: '#4285f4', textDecoration: 'none' }}>
                Go to Services
              </a>
            </div>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};
